import React, { useState, useEffect } from "react";
import { Grid, Paper } from "@material-ui/core";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import ProgressBar from "../progress-bar";
import StepInitial from "./StepInitial";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
// import useAuthContext from "../../hooks/useAuthContext";
import { injectIntl } from "react-intl";
import Content from "../content";
import Link from "../link";
import Button from "../button";
import Typography from "../typography";
import { ITeam, IMatch } from "../../types";

export const NEW_MATCH = gql`
  mutation NewMatch(
    $location: String!
    $date: String!
    $time: String!
    $homeTeam: ID!
    $awayTeam: String!
    $isHomeTeamLocal: Boolean!
    $isFriendly: Boolean!
  ) {
    createMatch(
      input: {
        location: $location
        date: $date
        time: $time
        homeTeam: $homeTeam
        awayTeam: $awayTeam
        isHomeTeamLocal: $isHomeTeamLocal
        isFriendly: $isFriendly
      }
    ) {
      location
      date
      time
    }
  }
`;

const ALL_TEAMS = gql`
  query AllTeams {
    allTeams {
      id
      name
      location
    }
  }
`;

const StyledActionLink = styled(Link)(({ theme }: IStyledArguments) => {
  return {
    ...theme.typography.caption,
    color: theme.palette.secondary.dark,
    textDecoration: "none",
    textTransform: "uppercase",
  };
});

const ButtonRow = styled("div")(({ theme }: IStyledArguments) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  paddingTop: theme.spacing(3),
  "& button": {
    padding: "16px 48px",
    fontSize: "1.1rem",
    fontWeight: 700,
    borderRadius: "16px",
    background: theme.palette.primary.main,
    color: "#fff",
    letterSpacing: "1px",
    border: "none",
    transition: "background 0.2s ease",
    "&:hover": {
      background: theme.palette.primary.dark,
    },
  },
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(2),
    "& button": {
      padding: "14px 40px",
      fontSize: "1rem",
    },
  },
}));

const MessageSpace = styled("div")(({ theme }: IStyledArguments) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  paddingTop: theme.spacing(3),
  gap: theme.spacing(2),
}));

const SuccessTitle = styled(Typography.H5)(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  fontSize: "1.8rem",
  letterSpacing: "-0.5px",
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.5rem",
  },
}));

const SuccessMessage = styled(Typography.Body)(({ theme }: IStyledArguments) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  fontSize: "1.1rem",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
  },
}));

const LogoContainer = styled("div")(({ theme }: IStyledArguments) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  marginBottom: theme.spacing(2),
}));

const Logo = styled("img")(({ theme }: IStyledArguments) => ({
  height: "80px",
  [theme.breakpoints.down("sm")]: {
    height: "60px",
  },
}));

const StyledPaper = styled(Paper)(({ theme }: IStyledArguments) => ({
  boxSizing: "border-box",
  padding: theme.spacing(5),
  marginBottom: theme.spacing(5),
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  margin: "0 auto",
  maxWidth: 630,
  borderRadius: "18px",
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3),
    borderRadius: "16px",
  },
}));

const PageNewMatch = () => {
  const { data: dataTeams } = useQuery(ALL_TEAMS);
  const [currentStep, setCurrentStep] = useState(1);
  const [match, setMatch] = useState<IMatch>();
  const [createMatch, { data, loading }] = useMutation(NEW_MATCH);
  const [teams, setTeams] = useState<ITeam[]>();

  const handleCreateMatch = async (form: any) => {
    console.log("creating a match...");
    console.log(form);

    const isLocal = form.isHomeTeamLocal;

    createMatch({
      variables: {
        location: form.location,
        date: form.date,
        time: form.time,
        homeTeam: form.homeTeam,
        awayTeam: form.awayTeam,
        isHomeTeamLocal: isLocal,
        isFriendly: form.isFriendly || false,
      },
    });
  };

  useEffect(() => {
    if (loading) {
      return;
    }

    if (dataTeams) {
      console.log("data teams recieved: ", dataTeams);
      setTeams(dataTeams.allTeams);
    }

    if (!data) {
      return;
    } else {
      setMatch(data.createMatch);
      setCurrentStep(2);
    }
  }, [data, dataTeams]);

  if (loading) {
    // TODO: LOADING SPINNER
    return (
      <Content>
        <p>LOADING</p>
      </Content>
    );
  }

  return (
    <Content>
      <Grid container spacing={0} justify="center" alignItems="center">
        <Grid item xs={12} md={6}>
          <StyledPaper>
            <ProgressBar steps={2} activeStep={currentStep} />
            {currentStep === 1 && (
              <StepInitial
                onGoNext={(values: any) => {
                  handleCreateMatch(values);
                }}
                match={match}
                teams={teams}
              />
            )}
            {currentStep === 2 && (
              <Content>
                <LogoContainer>
                  <Logo src="/images/footik-logo.svg" alt="Footik Logo" />
                </LogoContainer>
                <MessageSpace>
                  <SuccessTitle>¡Match Creado!</SuccessTitle>
                  <SuccessMessage>
                    {match && match.homeTeam} vs {match && match.awayTeam}
                  </SuccessMessage>
                </MessageSpace>
                <ButtonRow>
                  <StyledActionLink to="/people/matches">
                    <Button>Ok</Button>
                  </StyledActionLink>
                </ButtonRow>
              </Content>
            )}
          </StyledPaper>
        </Grid>
      </Grid>
    </Content>
  );
};

export default injectIntl(PageNewMatch);
